import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, BookOpen, Clock, Calendar, User, ChevronRight, Phone, MessageSquare, Info, ShieldAlert } from 'lucide-react';
import { Button } from '../../components/ui/button';
import JsonLd from '../../components/JsonLd';
import { breadcrumbSchema } from '../../utils/seoSchemas';
import { articleSchema } from '../../utils/articleSchema';

/**
 * Shared page shell for every WPAA Academy article.
 * Provides: hero, breadcrumb, standard Reader Guide (top),
 * TOC sidebar, inline CTA and standard Disclaimer (bottom).
 * Individual guides supply their body via `children`.
 */

const ReaderGuide = () => (
  <div className="rounded-xl border border-teal-100 bg-gradient-to-r from-teal-50 to-emerald-50 p-5 md:p-6 mb-8 not-prose">
    <div className="flex items-start gap-3">
      <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center flex-shrink-0 border border-teal-100">
        <Info size={20} className="text-teal-600" />
      </div>
      <div>
        <p className="font-bold text-gray-900 mb-1">How to use this guide</p>
        <p className="text-sm text-gray-700 leading-relaxed">
          This guide is written in plain English for UAE business owners, finance teams and compliance officers. Use the table of contents on the right to jump to a specific section, or read straight through for a full picture. Have a question specific to your business? Book a free consultation with our team using the buttons at the bottom of this page.
        </p>
      </div>
    </div>
  </div>
);

const Disclaimer = () => (
  <div className="mt-10 rounded-xl border border-gray-200 bg-gray-50 p-5 md:p-6 not-prose" data-testid="ka-disclaimer">
    <div className="flex items-start gap-3">
      <ShieldAlert size={18} className="text-gray-500 flex-shrink-0 mt-0.5" />
      <div>
        <p className="text-xs font-bold uppercase tracking-wider text-gray-600 mb-2">Disclaimer</p>
        <p className="text-xs text-gray-600 leading-relaxed">
          This guide is provided for general informational purposes only and should not be considered accounting, tax, legal, or professional advice. Please consult White Paper Accounts Auditing (WPAA) or another qualified professional before acting on any information contained in this guide.
        </p>
      </div>
    </div>
  </div>
);

const ArticleShell = ({ guide, toc = [], testId, ctaService, heroImage, children }) => {
  const path = `/knowledge-academy/${guide.slug}`;

  return (
    <div className="min-h-screen bg-white" data-testid={testId}>
      <JsonLd
        id={`ld-${guide.slug}-article`}
        data={articleSchema({
          headline: guide.title,
          description: guide.metaDescription,
          path,
          author: 'White Paper Accounts Auditing',
          dateModified: guide.updated
        })}
      />
      <JsonLd
        id={`ld-${guide.slug}-breadcrumb`}
        data={breadcrumbSchema([
          { name: 'Home', path: '/' },
          { name: 'WPAA Academy', path: '/knowledge-academy' },
          { name: guide.title, path }
        ])}
      />

      {/* HERO */}
      <section className="pt-32 pb-10 px-4 bg-gradient-to-br from-slate-900 via-teal-900 to-slate-900 text-white">
        <div className="container mx-auto max-w-4xl">
          <div className="flex items-center text-sm text-teal-200 mb-5" aria-label="Breadcrumb">
            <Link to="/knowledge-academy" className="hover:text-white flex items-center gap-1">
              <BookOpen size={14} /> WPAA Academy
            </Link>
            <ChevronRight size={14} className="mx-2" />
            <span className="text-white">{guide.category}</span>
          </div>
          <span className="inline-block bg-teal-500/20 text-teal-200 text-xs font-semibold px-3 py-1 rounded-full mb-3 uppercase tracking-wide">
            {guide.category}
          </span>
          <h1 className="text-3xl md:text-5xl font-bold mb-3 leading-tight">{guide.title}</h1>
          {guide.subtitle && <p className="text-xl text-teal-200 mb-4">{guide.subtitle}</p>}
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-gray-300">
            <span className="flex items-center gap-1.5"><User size={14} /> White Paper Accounts Auditing</span>
            <span className="flex items-center gap-1.5"><Calendar size={14} /> Updated {guide.updated
              ? new Date(guide.updated).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' })
              : new Date().toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}</span>
            <span className="flex items-center gap-1.5"><Clock size={14} /> {guide.readingTime}</span>
          </div>
        </div>
      </section>

      {/* Optional featured image — explicit dimensions + async decoding +
          high fetch priority keep LCP fast and prevent any layout shift. */}
      {heroImage?.src && (
        <section className="px-4 -mt-6 mb-2 md:mb-4">
          <div className="container mx-auto max-w-4xl">
            <img
              src={heroImage.src}
              alt={heroImage.alt || guide.title}
              width={heroImage.width || 1920}
              height={heroImage.height || 960}
              loading="eager"
              decoding="async"
              fetchPriority="high"
              className="w-full h-auto rounded-xl shadow-xl ring-1 ring-black/5 bg-white"
            />
          </div>
        </section>
      )}

      {/* CONTENT + TOC */}
      <section className="py-10 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_260px] gap-10">
            <article className="prose prose-lg max-w-none min-w-0">
              <details className="lg:hidden not-prose mb-8 border border-gray-200 rounded-xl bg-gray-50 group" data-testid="ka-mobile-toc">
                <summary className="cursor-pointer list-none px-5 py-4 text-sm font-bold uppercase tracking-wider text-teal-700 flex items-center justify-between">
                  On this page <ArrowRight size={16} className="transition-transform group-open:rotate-90" />
                </summary>
                <ul className="px-5 pb-4 space-y-2 text-sm">
                  {toc.map((t) => (
                    <li key={t.id}>
                      <a href={`#${t.id}`} className="text-gray-700 hover:text-teal-700 block leading-snug" data-testid={`ka-mobile-toc-${t.id}`}>{t.label}</a>
                    </li>
                  ))}
                </ul>
              </details>
              <ReaderGuide />
              {children}

              {/* Inline CTA */}
              <div className="mt-10 p-8 rounded-2xl bg-gradient-to-r from-teal-700 to-emerald-700 text-white not-prose">
                <h3 className="text-2xl font-bold mb-2">Need help with {guide.category}?</h3>
                <p className="text-teal-100 mb-5">
                  Book a free 30-minute consultation with our team. We&apos;ll review your specific situation and flag anything worth acting on.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Link to={`/contact?service=${ctaService}`}>
                    <Button size="lg" className="bg-white text-teal-700 hover:bg-gray-100" data-testid={`${guide.slug}-cta-consult`}>
                      Book Free Consultation <ArrowRight size={16} className="ml-2" />
                    </Button>
                  </Link>
                  <a href="https://wa.me/971501219341" target="_blank" rel="noopener noreferrer" data-testid={`${guide.slug}-cta-wa`}>
                    <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                      <MessageSquare size={16} className="mr-2" /> WhatsApp Us
                    </Button>
                  </a>
                  <a href="tel:+971501219341" data-testid={`${guide.slug}-cta-call`}>
                    <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                      <Phone size={16} className="mr-2" /> Call Now
                    </Button>
                  </a>
                </div>
              </div>

              <div className="mt-8 border-t border-gray-200 pt-5 flex items-center justify-between not-prose">
                <Link to="/knowledge-academy" className="inline-flex items-center gap-2 text-teal-700 font-semibold hover:text-teal-800">
                  <ArrowLeft size={16} /> Back to WPAA Academy
                </Link>
                {guide.relatedService && (
                  <Link to={guide.relatedService.path} className="inline-flex items-center gap-2 text-teal-700 font-semibold hover:text-teal-800">
                    Explore {guide.relatedService.label} <ArrowRight size={16} />
                  </Link>
                )}
              </div>

              <Disclaimer />
            </article>

            {/* TOC sidebar */}
            <aside className="hidden lg:block">
              <div className="sticky top-24">
                <div className="border border-gray-200 rounded-xl p-5 bg-gray-50">
                  <p className="text-xs font-bold uppercase tracking-wider text-teal-700 mb-3">On this page</p>
                  <ul className="space-y-2 text-sm">
                    {toc.map((t) => (
                      <li key={t.id}>
                        <a
                          href={`#${t.id}`}
                          className="text-gray-700 hover:text-teal-700 hover:font-medium transition-colors block leading-snug"
                        >
                          {t.label}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ArticleShell;
